<template>
    <div class="admin-download-logs">
        <div class="page-header">
            <div>
                <h2>下载列表</h2>
                <p class="subtitle">查看每个用户下载或购买过的组卷、试卷资料</p>
            </div>
            <el-button :icon="Refresh" :loading="loading" @click="fetchLogs">刷新</el-button>
        </div>

        <el-card shadow="never" class="filter-card">
            <el-form :model="filters" inline>
                <el-form-item label="用户">
                    <el-input v-model="filters.user" clearable placeholder="用户名或用户ID" style="width: 180px"
                        @keyup.enter="handleSearch" />
                </el-form-item>
                <el-form-item label="资料">
                    <el-input v-model="filters.keyword" clearable placeholder="试卷名/组卷内容" style="width: 220px"
                        @keyup.enter="handleSearch" />
                </el-form-item>
                <el-form-item label="类型">
                    <el-select v-model="filters.resourceType" clearable placeholder="全部类型" style="width: 150px">
                        <el-option label="试卷资料" value="paper" />
                        <el-option label="自定义组卷" value="custom_paper" />
                    </el-select>
                </el-form-item>
                <el-form-item label="状态">
                    <el-select v-model="filters.payStatus" clearable placeholder="全部状态" style="width: 150px">
                        <el-option label="已下载" value="downloaded" />
                        <el-option label="已支付" value="paid" />
                        <el-option label="免费" value="free" />
                    </el-select>
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" :icon="Search" @click="handleSearch">查询</el-button>
                    <el-button @click="handleReset">重置</el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <el-card shadow="never" class="table-card">
            <template #header>
                <div class="table-header">
                    <span>下载记录</span>
                    <el-tag type="info" effect="plain">共 {{ total }} 条</el-tag>
                </div>
            </template>

            <el-table :data="logs" v-loading="loading" border stripe style="width: 100%">
                <el-table-column label="用户" min-width="180">
                    <template #default="{ row }">
                        <div class="user-cell">
                            <el-avatar :size="30" :src="row.avatar">{{ row.username?.charAt(0) }}</el-avatar>
                            <div class="user-info">
                                <span class="username">{{ row.username || '-' }}</span>
                                <span class="muted">ID: {{ row.userId }}</span>
                            </div>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="类型" width="120">
                    <template #default="{ row }">
                        <el-tag :type="row.resourceType === 'custom_paper' ? 'success' : 'primary'" effect="plain">
                            {{ resourceTypeLabel(row.resourceType) }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="下载/购买资料" min-width="260" show-overflow-tooltip>
                    <template #default="{ row }">
                        <div class="resource-cell">
                            <span class="resource-title">{{ row.resourceTitle || resourceTypeLabel(row.resourceType) }}</span>
                            <span class="muted" v-if="row.resourceId">资源ID: {{ row.resourceId }}</span>
                            <span class="muted" v-else-if="row.contentHash">组卷哈希: {{ shortHash(row.contentHash) }}</span>
                        </div>
                    </template>
                </el-table-column>

                <el-table-column label="金额" width="110" align="right">
                    <template #default="{ row }">
                        <span>{{ formatMoney(row.cashAmount) }}</span>
                    </template>
                </el-table-column>

                <el-table-column label="积分" width="90" align="right">
                    <template #default="{ row }">
                        <span>{{ row.pointsUsed || 0 }}</span>
                    </template>
                </el-table-column>

                <el-table-column label="状态" width="105">
                    <template #default="{ row }">
                        <el-tag :type="statusType(row.payStatus)" size="small">
                            {{ statusLabel(row.payStatus) }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="来源" width="110">
                    <template #default="{ row }">
                        <el-tag :type="row.source === 'log' ? 'warning' : 'info'" effect="plain" size="small">
                            {{ row.source === 'log' ? '实际下载' : '订单记录' }}
                        </el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="时间" width="170">
                    <template #default="{ row }">{{ formatTime(row.createdAt) }}</template>
                </el-table-column>
            </el-table>

            <div class="pagination-row">
                <el-pagination v-model:current-page="page" v-model:page-size="pageSize" :total="total"
                    :page-sizes="[20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
                    @size-change="handlePageSizeChange" @current-change="fetchLogs" />
            </div>
        </el-card>
    </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { Refresh, Search } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import api from '@/utils/api'

const loading = ref(false)
const logs = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(20)

const filters = reactive({
    user: '',
    keyword: '',
    resourceType: '',
    payStatus: ''
})

const fetchLogs = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/admin/download-logs', {
            params: {
                page: page.value,
                pageSize: pageSize.value,
                user: filters.user || undefined,
                keyword: filters.keyword || undefined,
                resourceType: filters.resourceType || undefined,
                payStatus: filters.payStatus || undefined
            }
        })
        logs.value = res.data.logs || []
        total.value = res.data.total || 0
    } catch (err) {
        ElMessage.error('加载下载列表失败')
    } finally {
        loading.value = false
    }
}

const handleSearch = () => {
    page.value = 1
    fetchLogs()
}

const handleReset = () => {
    filters.user = ''
    filters.keyword = ''
    filters.resourceType = ''
    filters.payStatus = ''
    page.value = 1
    fetchLogs()
}

const handlePageSizeChange = () => {
    page.value = 1
    fetchLogs()
}

const resourceTypeLabel = (type) => {
    const map = {
        paper: '试卷资料',
        custom_paper: '自定义组卷'
    }
    return map[type] || type || '-'
}

const statusLabel = (status) => {
    const map = {
        downloaded: '已下载',
        paid: '已支付',
        free: '免费',
        pending: '待支付'
    }
    return map[status] || status || '-'
}

const statusType = (status) => {
    const map = {
        downloaded: 'warning',
        paid: 'success',
        free: 'primary',
        pending: 'info'
    }
    return map[status] || 'info'
}

const formatMoney = (amount) => {
    const cents = Number(amount || 0)
    if (cents <= 0) return '¥0.00'
    return `¥${(cents / 100).toFixed(2)}`
}

const shortHash = (hash) => {
    if (!hash) return '-'
    return hash.length > 12 ? `${hash.slice(0, 12)}...` : hash
}

const formatTime = (time) => {
    if (!time) return '-'
    const date = new Date(time)
    return date.getFullYear() + '-' +
        String(date.getMonth() + 1).padStart(2, '0') + '-' +
        String(date.getDate()).padStart(2, '0') + ' ' +
        String(date.getHours()).padStart(2, '0') + ':' +
        String(date.getMinutes()).padStart(2, '0') + ':' +
        String(date.getSeconds()).padStart(2, '0')
}

onMounted(fetchLogs)
</script>

<style scoped>
.admin-download-logs {
    padding: 0;
}

.page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
}

.page-header h2 {
    margin: 0;
    font-size: 24px;
    color: #1f2937;
}

.subtitle {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 14px;
}

.filter-card,
.table-card {
    border-radius: 8px;
    border: 1px solid #e5e7eb;
}

.filter-card {
    margin-bottom: 16px;
}

.filter-card :deep(.el-form-item) {
    margin-bottom: 0;
}

.table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 700;
    color: #1f2937;
}

.user-cell {
    display: flex;
    align-items: center;
    gap: 10px;
}

.user-info,
.resource-cell {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.username,
.resource-title {
    color: #1f2937;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.muted {
    color: #94a3b8;
    font-size: 12px;
}

.pagination-row {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
}
</style>
